/**
 * Catalogue des SKILLCARDS qu'un prof peut donner à un élève (RPC
 * `get_card_catalog_for_student`, migration 0354) : cartes de niveau
 * compatible regroupées — pack en cours, packs passés, packs à venir, hors
 * pack, niveaux inférieurs. `catalogSections` (PUR, testé) en fait les
 * sections du sélecteur, sans les cartes déjà données.
 */
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { packPeriodLabel } from '@jeraisonne/db/curriculum';
import { formatCardLevels } from '@jeraisonne/ui/tool-cards';
import type { CardColorTheme, CardRarity } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';
import type { SkillcardPickerItem } from '@/features/skillcards/skillcard-picker';

const db = supabase as unknown as SupabaseClient;

export interface CatalogCard {
  id: string;
  title: string;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  domain_label: string | null;
  levels: string[];
  assigned: boolean;
}

export type CatalogGroup =
  | {
      kind: 'pack';
      phase: 'current' | 'past' | 'upcoming';
      pack_id: string;
      title: string;
      start_month: number;
      end_month: number | null;
      cards: CatalogCard[];
    }
  | { kind: 'off_pack' | 'lower'; cards: CatalogCard[] };

export interface CardCatalog {
  student_level: string | null;
  groups: CatalogGroup[];
}

export interface PickerSection {
  key: string;
  label: string;
  items: SkillcardPickerItem[];
}

export const cardCatalogKey = (studentId: string) => ['card-catalog', studentId] as const;

export function useCardCatalogForStudent(studentId: string | null) {
  return useQuery({
    queryKey: cardCatalogKey(studentId ?? ''),
    enabled: !!studentId,
    staleTime: 60_000,
    queryFn: async (): Promise<CardCatalog> => {
      const { data, error } = await db.rpc('get_card_catalog_for_student', {
        p_student_id: studentId,
      });
      if (error) throw new Error(error.message);
      const c = (data ?? {}) as Partial<CardCatalog>;
      return { student_level: c.student_level ?? null, groups: c.groups ?? [] };
    },
  });
}

export function catalogCardToPickerItem(c: CatalogCard): SkillcardPickerItem {
  return {
    id: c.id,
    title: c.title,
    levels: c.levels ?? [],
    rarity: c.rarity,
    colorTheme: c.color_theme,
    creature: c.creature,
    domainLabel: c.domain_label,
  };
}

const PHASE_LABELS = { current: 'En ce moment', past: 'Pack passé', upcoming: 'À venir' } as const;

/** Libellé d'une section du sélecteur. */
export function catalogGroupLabel(group: CatalogGroup, studentLevel: string | null): string {
  if (group.kind === 'pack') {
    const period = packPeriodLabel(group.start_month, group.end_month);
    return `${PHASE_LABELS[group.phase]} · ${group.title} (${period})`;
  }
  if (group.kind === 'off_pack') {
    return studentLevel
      ? `Hors pack · ${formatCardLevels([studentLevel])}`
      : 'Toutes les SKILLCARDS';
  }
  return 'Niveaux inférieurs';
}

/**
 * Sections du sélecteur, dans l'ordre du serveur (en cours → passés → à venir
 * → hors pack → niveaux inférieurs). Une carte déjà donnée à l'élève est
 * retirée ; une carte présente dans plusieurs packs n'apparaît qu'une fois
 * (dans le premier). Les sections vides disparaissent.
 */
export function catalogSections(catalog: CardCatalog | null | undefined): PickerSection[] {
  if (!catalog) return [];
  const seen = new Set<string>();
  const out: PickerSection[] = [];
  catalog.groups.forEach((g, i) => {
    const items = g.cards
      .filter((c) => !c.assigned && !seen.has(c.id))
      .map((c) => {
        seen.add(c.id);
        return catalogCardToPickerItem(c);
      });
    if (items.length === 0) return;
    out.push({
      key: g.kind === 'pack' ? `pack-${g.pack_id}` : `${g.kind}-${i}`,
      label: catalogGroupLabel(g, catalog.student_level),
      items,
    });
  });
  return out;
}
